import { useContext, useEffect, useMemo, useState } from "react";
import { Input, Form, Select, Row, Col } from "antd";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Updates, {
  ReleaseUpdateTypeOptions,
  ReleaseUpdateTypes,
} from "../../Services/UpdatesService";
import { useForm } from "antd/es/form/Form";
import { AppDrawer } from "../../Common/Layout";
import { adminContext } from "../../Common/PageRoute";

export interface UpdateDetailsProps {
  onDismiss: (confirmed: boolean) => void;
  record: any;
  type: "add" | "edit";
  defaultVersion: any;
}

const UpdateDetails = ({
  onDismiss,
  record,
  type,
  defaultVersion,
}: UpdateDetailsProps) => {
  const { openNotification } = useContext(adminContext);
  const [form]: any = useForm();
  const [loading, setLoading] = useState(false);

  const generatedVersion = useMemo(() => {
    if (type === "edit") return record?.version;

    const now = new Date();
    const yy = now.getFullYear() % 100;
    const mm = now.getMonth() + 1;

    const last = defaultVersion?.version;

    if (!last) return `${yy}.${mm}.1`;

    const [lastYY, lastMM, lastCount] = last.split(".").map(Number);

    if (lastYY === yy && lastMM === mm) {
      return `${yy}.${mm}.${(lastCount || 0) + 1}`;
    }

    return `${yy}.${mm}.1`;
  }, [type, record, defaultVersion]);

  useEffect(() => {
    if (type === "edit") {
      form.setFieldsValue({
        version: record?.version ?? "",
        type: record?.type ?? ReleaseUpdateTypes.Enhancement,
        title: record?.title ?? "",
        description: record?.description ?? "",
        content: record?.content ?? "",
      });
    } else {
      form.setFieldsValue({
        version: generatedVersion,
        type: ReleaseUpdateTypes.Enhancement,
        title: "",
        description: "",
        content: "",
      });
    }
  }, [type, record, generatedVersion, form]);

  const handleSave = () => {
    const id =
      type === "edit" ? record?.id || record?.Id || record?._id || "" : "";
    setLoading(true);
    form
      .validateFields()
      .then(async (values: any) => {
        try {
          const res = await Updates.addUpdate(id, values);
          setLoading(false);
          if (res?.status) {
            openNotification(
              "success",
              type === "edit"
                ? "Release updated successfully"
                : "Release added successfully",
            );
            onDismiss(true);
          } else {
            openNotification("error", res?.message || "Failed to save release");
          }
        } catch (err: any) {
          setLoading(false);
          openNotification(
            "error",
            err?.message || "An unexpected error occurred",
          );
        }
      })
      .catch(() => setLoading(false));
  };

  const initialDescription = type === "edit" ? record?.description || "" : "";
  const initialContent = type === "edit" ? record?.content || "" : "";

  return (
    <AppDrawer
      title={type === "edit" ? "Edit Release" : "New Release"}
      subtitle={
        type === "edit"
          ? `${record?.version || ""} • ${record?.title || ""}`
          : "Create a new release note and version details"
      }
      width={700}
      open={true}
      onClose={() => onDismiss(false)}
      onSave={handleSave}
      saveText="Save"
      cancelText="Cancel"
      saveButtonProps={{ loading }}
      cancelButtonProps={{ disabled: loading }}
    >
      <style>{`
        .release-drawer-form .ck.ck-editor {
          width: 100% !important;
          max-width: 100% !important;
        }
        .release-drawer-form .ck-editor__editable {
          min-height: 120px !important;
          border-bottom-left-radius: 6px !important;
          border-bottom-right-radius: 6px !important;
        }
        .release-drawer-form .ck-toolbar {
          border-top-left-radius: 6px !important;
          border-top-right-radius: 6px !important;
        }
      `}</style>
      <Form
        form={form}
        layout="vertical"
        requiredMark={true}
        className="release-drawer-form"
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Version" name="version">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: "Select release type" }]}
            >
              <Select
                placeholder="Select type"
                options={ReleaseUpdateTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input
            placeholder="Release title"
            onChange={(e) => {
              const value = e.target.value;
              const capitalized =
                value.charAt(0).toUpperCase() + value.slice(1);
              form.setFieldsValue({ title: capitalized });
            }}
          />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <div className="ckeditor-description">
            <CKEditor
              editor={ClassicEditor as any}
              data={initialDescription}
              config={{
                initialData: initialDescription,
              }}
              onChange={(e: any, editor: any) =>
                form.setFieldsValue({ description: editor.getData() })
              }
            />
          </div>
        </Form.Item>

        <Form.Item label="Content" name="content">
          <div className="ckeditor-container">
            <CKEditor
              editor={ClassicEditor as any}
              data={initialContent}
              config={{
                initialData: initialContent,
              }}
              onChange={(e: any, editor: any) =>
                form.setFieldsValue({ content: editor.getData() })
              }
            />
          </div>
        </Form.Item>
      </Form>
    </AppDrawer>
  );
};

export default UpdateDetails;
